import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Typography, Skeleton } from '@mui/material';
import GameViewLeaderBoard from './gameViewLeaderBoard.tsx';
import { GameViewMapping, intialGameViewMapping } from '../../utils/gameViewMapping.ts';
import VotingService from '../../api_handlers/services/VotingService.ts';
import { useAppSelector } from '../../redux/hook.ts';

interface GameViewModalProps {
    open: boolean;
    onClose: () => void;
    moveToNextCollectible: () => void;
    moveToPrev: (arg0: number) => void;
    startVoting: () => void;
    moveToNextTeam: () => void;
    currentIndex: any;
}

const GameViewModal = ({ open, onClose, moveToNextCollectible, moveToPrev, startVoting, moveToNextTeam, currentIndex }: GameViewModalProps) => {

    const fSession = useAppSelector((state) => state.fSession.session);

    const [view] = useState<Array<GameViewMapping>>(intialGameViewMapping);
    const [currentVotingTeam, setCurrentVotingTeam] = useState<string>('');
    const [showLeaderBoard, setShowLeaderBoard] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
    const isScoringSession = currentIndex <= 12;

    const getCurrentVotingTeam = useCallback(async () => {
        try {
            const sessionId = fSession?.sessionId;
            const data = await VotingService.getTeamForVoting(sessionId);
            if (data?.message) {
                setCurrentVotingTeam("Completed");
            } else {
                setCurrentVotingTeam(data?.data?.team?.teamName);
            }
        } catch (error) {
            // showToast(error.response.data, "warning");
        }
    }, [fSession]);

    const handleNext = () => {
        if (currentIndex === 11) {
            moveToNextCollectible();
            setIsLoading(true);
            setTimeout(() => {
                startVoting();
                setIsLoading(false);
            }, 5000);
        } else {
            moveToNextCollectible();
        }
    };

    const handleNextVotingTeam = () => {
        // Show confirmation only the first time (when transitioning from self-voting to team voting)
        if (currentIndex === 12) {
            setShowConfirmDialog(true);
        } else {
            // For subsequent teams, proceed without confirmation
            moveToNextTeam();
            getCurrentVotingTeam();
        }
    };

    const confirmMoveToTeamVoting = () => {
        setShowConfirmDialog(false);
        moveToNextCollectible(); // This moves from index 12 to 13
        moveToNextTeam();
        getCurrentVotingTeam();
    };

    const cancelMoveToTeamVoting = () => {
        setShowConfirmDialog(false);
    };

    useEffect(() => {
        if (currentIndex === 13) {
            getCurrentVotingTeam();
        }
    }, [currentIndex, getCurrentVotingTeam]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={showLeaderBoard ? "lg" : "md"}
            sx={{
                backgroundColor: "rgba(0, 0, 0, 0.8)",
                '& .MuiDialog-paper': {
                    background: 'linear-gradient(135deg, rgba(139, 69, 19, 0.98) 0%, rgba(101, 67, 33, 0.95) 100%)',
                    backdropFilter: 'blur(10px)',
                    border: '0.25rem solid rgba(218, 165, 32, 0.7)',
                    borderRadius: '1rem',
                    boxShadow: '0 1rem 3rem rgba(0, 0, 0, 0.9), 0 0 2rem rgba(218, 165, 32, 0.5)',
                }
            }}
            fullWidth
        >
            <div className='px-6 py-4'>
                <Typography fontWeight={"bold"} variant="h5" marginBottom={2} sx={{
                    fontFamily: "'Cinzel', serif",
                    color: '#FFD700',
                    textShadow: '0.15rem 0.15rem 0.3rem rgba(0,0,0,0.9), 0 0 0.8rem rgba(255, 215, 0, 0.6)',
                }}>Game View</Typography>

                {showLeaderBoard ? (
                    <GameViewLeaderBoard sessionId={fSession?.sessionId} />
                ) : (
                    <>
                        {isScoringSession ? (
                            <Box
                                padding={3}
                                display={"flex"}
                                flexDirection="column"
                                gap={3}
                                alignItems={"center"}
                                border={"0.2rem solid rgba(218, 165, 32, 0.6)"}
                                borderRadius={'1rem'}
                                sx={{
                                    background: 'linear-gradient(135deg, rgba(139, 69, 19, 0.3) 0%, rgba(101, 67, 33, 0.25) 100%)',
                                    minHeight: '20rem',
                                }}
                            >
                                {isLoading ? (
                                    <>
                                        <Skeleton variant="circular" width={96} height={96} />
                                        <Skeleton variant="text" width={201} height={40} />
                                        <Skeleton variant="text" width={300} height={24} />
                                    </>
                                ) : (
                                    <>
                                        <img
                                            className='animate-rotate-3d'
                                            src={`/img/items-icons/${view[currentIndex].imgSrc}.png`}
                                            alt='standardCredits'
                                            style={{ width: '10rem', height: '10rem' }}
                                        />
                                        <Typography fontWeight={"bold"} variant="h4" sx={{
                                            fontFamily: "'Cinzel', serif",
                                            color: '#FFD700',
                                            textShadow: '0.15rem 0.15rem 0.3rem rgba(0,0,0,0.9), 0 0 0.8rem rgba(255, 215, 0, 0.6)',
                                        }}>
                                            {view[currentIndex].item.charAt(0).toUpperCase() + view[currentIndex].item.slice(1)}
                                        </Typography>
                                        <Typography fontWeight={"400"} variant="h6" marginBottom={2} textAlign={"center"} sx={{
                                            fontFamily: "'Philosopher', sans-serif",
                                            color: '#FFA500',
                                            fontSize: '1.25rem',
                                        }}>
                                            {view[currentIndex].text}
                                        </Typography>
                                    </>
                                )}
                            </Box>
                        ) : (
                            <Box
                                padding={3}
                                display={"flex"}
                                flexDirection="column"
                                gap={3}
                                alignItems={"center"}
                                border={"0.2rem solid rgba(218, 165, 32, 0.6)"}
                                borderRadius={'1rem'}
                                sx={{
                                    background: 'linear-gradient(135deg, rgba(139, 69, 19, 0.3) 0%, rgba(101, 67, 33, 0.25) 100%)',
                                    minHeight: '20rem',
                                }}
                            >
                                {currentVotingTeam === "Completed" ? (
                                    <>
                                        <Typography fontWeight={"bold"} variant="h4" sx={{
                                            fontFamily: "'Cinzel', serif",
                                            color: '#FFD700',
                                            textShadow: '0.15rem 0.15rem 0.3rem rgba(0,0,0,0.9), 0 0 0.8rem rgba(255, 215, 0, 0.6)',
                                        }}>Voting Session Completed</Typography>
                                        <Button variant='outlined' sx={{
                                            mt: 2,
                                            fontFamily: "'Cinzel', serif",
                                            fontWeight: 700,
                                            color: '#FFD700',
                                            borderColor: 'rgba(218, 165, 32, 0.7)',
                                            '&:hover': {
                                                borderColor: '#FFD700',
                                                background: 'rgba(218, 165, 32, 0.2)',
                                            }
                                        }} onClick={() => setShowLeaderBoard(true)}>
                                            Show LeaderBoard
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <img
                                            className='animate-rotate-3d'
                                            src={`/img/team-logo/${currentVotingTeam}.png`}
                                            alt='team logo'
                                            style={{ width: '10rem', height: '10rem' }}
                                        />
                                        <Typography fontWeight={"bold"} variant="h4" sx={{
                                            fontFamily: "'Cinzel', serif",
                                            color: '#FFD700',
                                            textShadow: '0.15rem 0.15rem 0.3rem rgba(0,0,0,0.9), 0 0 0.8rem rgba(255, 215, 0, 0.6)',
                                        }}>
                                            {currentVotingTeam}
                                        </Typography>
                                        <Typography fontWeight={"400"} variant="h6" marginBottom={2} textAlign={"center"} sx={{
                                            fontFamily: "'Philosopher', sans-serif",
                                            color: '#FFA500',
                                            fontSize: '1.15rem',
                                        }}>
                                            {`As we begin the ${currentVotingTeam} Voting Session, do you think your team is trustworthy, values and esteems each other, holds interests in high regard, is honest and ethical, and has confidence in their abilities?`}
                                        </Typography>
                                    </>
                                )}
                            </Box>
                        )}

                        {currentIndex < 12 ? (
                            <div className='flex justify-between mt-4'>
                                <Button onClick={() => moveToPrev(currentIndex - 1)} disabled={currentIndex === 0} sx={{
                                    fontFamily: "'Cinzel', serif",
                                    fontWeight: 700,
                                    background: 'linear-gradient(135deg, #DAA520 0%, #B8860B 100%)',
                                    color: '#FFF',
                                    '&:hover': {
                                        background: 'linear-gradient(135deg, #FFD700 0%, #DAA520 100%)',
                                    },
                                    '&:disabled': {
                                        background: 'rgba(128, 128, 128, 0.3)',
                                    }
                                }}>
                                    Prev
                                </Button>
                                <Button onClick={handleNext} disabled={currentIndex === view.length - 1} sx={{
                                    fontFamily: "'Cinzel', serif",
                                    fontWeight: 700,
                                    background: 'linear-gradient(135deg, #DAA520 0%, #B8860B 100%)',
                                    color: '#FFF',
                                    '&:hover': {
                                        background: 'linear-gradient(135deg, #FFD700 0%, #DAA520 100%)',
                                    },
                                    '&:disabled': {
                                        background: 'rgba(128, 128, 128, 0.3)',
                                    }
                                }}>
                                    {`${currentIndex === 11 ? "Start Voting Session" : "Next"}`}
                                </Button>
                            </div>
                        ) : (
                            <>
                                {currentVotingTeam !== "Completed" && !isLoading && (
                                    <Box sx={{
                                        textAlign: 'center',
                                        mt: 4,
                                        p: 2,
                                        background: 'linear-gradient(135deg, rgba(218, 165, 32, 0.3) 0%, rgba(184, 134, 11, 0.2) 100%)',
                                        borderRadius: '1rem',
                                        border: '0.2rem solid rgba(255, 215, 0, 0.6)',
                                        boxShadow: '0 0 1.5rem rgba(218, 165, 32, 0.5)',
                                    }}>
                                        <Typography variant="body1" sx={{
                                            fontFamily: "'Philosopher', sans-serif",
                                            color: '#FFA500',
                                            mb: 2,
                                            fontSize: '1.1rem',
                                            fontWeight: 600,
                                        }}>
                                            {currentIndex === 12 ? '⚠️ Ready to start the Team Voting Session?' : '⚠️ Ready to move to the next team?'}
                                        </Typography>
                                        <Button onClick={handleNextVotingTeam} variant='contained' size="large" sx={{
                                            fontFamily: "'Cinzel', serif",
                                            fontWeight: 700,
                                            fontSize: '1.2rem',
                                            padding: '1rem 3rem',
                                            background: 'linear-gradient(135deg, #DAA520 0%, #B8860B 100%)',
                                            border: '0.2rem solid rgba(255, 215, 0, 0.8)',
                                            borderRadius: '0.75rem',
                                            boxShadow: '0 0.5rem 1.5rem rgba(218, 165, 32, 0.6), inset 0 0.125rem 0 rgba(255, 255, 255, 0.3)',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.1rem',
                                            animation: 'pulse-glow 2s ease-in-out infinite',
                                            '&:hover': {
                                                background: 'linear-gradient(135deg, #FFD700 0%, #DAA520 100%)',
                                                boxShadow: '0 0.75rem 2rem rgba(218, 165, 32, 0.8), inset 0 0.125rem 0 rgba(255, 255, 255, 0.4)',
                                                transform: 'translateY(-0.25rem) scale(1.05)',
                                            },
                                            '&:active': {
                                                transform: 'translateY(0) scale(1)',
                                            }
                                        }}>
                                            {currentIndex === 12 ? '🚀 Start Team Voting Session' : '🎯 Move to Next Voting Team'}
                                        </Button>
                                    </Box>
                                )}
                            </>
                        )}
                    </>
                )}
            </div>

            {/* Confirmation Dialog for first-time team voting transition */}
            <Dialog
                open={showConfirmDialog}
                onClose={cancelMoveToTeamVoting}
                maxWidth="sm"
                fullWidth
                sx={{
                    '& .MuiDialog-paper': {
                        background: 'linear-gradient(135deg, rgba(139, 69, 19, 0.98) 0%, rgba(101, 67, 33, 0.95) 100%)',
                        backdropFilter: 'blur(10px)',
                        border: '0.25rem solid rgba(218, 165, 32, 0.7)',
                        borderRadius: '1rem',
                        boxShadow: '0 1rem 3rem rgba(0, 0, 0, 0.9), 0 0 2rem rgba(218, 165, 32, 0.5)',
                    }
                }}
            >
                <DialogTitle sx={{
                    fontFamily: "'Cinzel', serif",
                    color: '#FFD700',
                    fontWeight: 700,
                    fontSize: '1.5rem',
                    textShadow: '0.15rem 0.15rem 0.3rem rgba(0,0,0,0.9), 0 0 0.8rem rgba(255, 215, 0, 0.6)',
                    textAlign: 'center',
                }}>
                    ⚠️ Confirm
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{
                        fontFamily: "'Philosopher', sans-serif",
                        color: '#FFA500',   
                        fontSize: '1.1rem',
                        textAlign: 'center',
                        lineHeight: 1.6,
                    }}>
                        Are you sure you want to proceed?
                        <br /><br />
                        <strong style={{ color: '#FFD700' }}>
                            This will end the Self-Voting session and move to Team Voting.
                        </strong>
                        <br /><br />
                        Once confirmed, teams will start voting for each other.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ padding: '1rem 1.5rem', justifyContent: 'center', gap: 2 }}>
                    <Button onClick={cancelMoveToTeamVoting} variant="contained" sx={{
                        fontFamily: "'Cinzel', serif",
                        fontWeight: 700,
                        background: 'linear-gradient(135deg, #808080 0%, #606060 100%)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #909090 0%, #707070 100%)',
                        },
                    }}>
                        Cancel
                    </Button>
                    <Button onClick={confirmMoveToTeamVoting} variant="contained" sx={{
                        fontFamily: "'Cinzel', serif",
                        fontWeight: 700,
                        background: 'linear-gradient(135deg, #DAA520 0%, #B8860B 100%)',
                        border: '0.15rem solid rgba(255, 215, 0, 0.6)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #FFD700 0%, #DAA520 100%)',
                            boxShadow: '0 0.5rem 1.5rem rgba(218, 165, 32, 0.6)',
                        },
                    }}>
                        ✓ Yes, Start Team Voting
                    </Button>
                </DialogActions>
            </Dialog>

        </Dialog>
    );
};

export default GameViewModal;
